import {AlertIcon, CommentIcon, InfoIcon, XCircleFillIcon} from '@primer/octicons-react'
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type MouseEvent,
  type ReactNode,
  type ReactElement,
  type KeyboardEvent,
} from 'react'
import {Tooltip} from '@primer/react/next'
import {Link, TreeView} from '@primer/react'
import {PortalTooltip} from '@github-ui/portal-tooltip/portalled'
import type {DiffDelta, DirectoryNode, FileNode} from './diff-file-tree-helpers'
import {caseFirstCompare, getFileTree} from './diff-file-tree-helpers'
import {FileStatusIcon} from './FileStatusIcon'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {useSelectedDiffHash} from '@github-ui/diffs/use-selected-diff-hash'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import styles from './DiffFileTree.module.css'

export const DIFF_FILE_TREE_ID = 'diff_file_tree'

export interface FileProps {
  file: FileNode<DiffDelta>
  depth: number
  onSelect?: () => void
  hash: string
}

const FileStatusIconItem = memo(function ({changeType, isSymlink}: {changeType: string; isSymlink?: boolean}) {
  return <FileStatusIcon status={changeType} isSymlink={isSymlink} />
})

FileStatusIconItem.displayName = 'FileStatusIconItem'

export const File = memo(function File({file, depth, hash, onSelect}: FileProps) {
  const rowRef = useRef<HTMLElement>(null)
  const anchorRef = useRef<HTMLAnchorElement>(null)
  const anchorHref = useMemo(() => `#diff-${file.diff.pathDigest}`, [file.diff.pathDigest])
  const showTooltip = useFileTreeTooltip({focusRowRef: rowRef, mouseRowRef: rowRef})
  const totalCommentsCount = file.diff.totalCommentsCount ?? 0
  const maxAnnotation = file.diff.highestAnnotationLevel

  const {screenReaderText, annotationElement} = useMemo(() => {
    let text = file.fileName ?? 'This file'
    let element = null

    if (totalCommentsCount > 0) {
      text += ` has ${totalCommentsCount < 10 ? totalCommentsCount : '9+'} ${
        totalCommentsCount > 1 ? 'comments' : 'comment'
      }`
    }

    switch (maxAnnotation) {
      case 'WARNING':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has warning annotations`
        element = (
          <div className="pl-1 fgColor-attention">
            <AlertIcon />
          </div>
        )
        break
      case 'NOTICE':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has notice annotations`
        element = (
          <div className="pl-1 fgColor-default">
            <InfoIcon />
          </div>
        )
        break
      case 'FAILURE':
        text += `${totalCommentsCount > 0 ? ' and' : ''} has failure annotations`
        element = (
          <div className="pl-1 fgColor-danger">
            <XCircleFillIcon />
          </div>
        )
        break
    }

    return {screenReaderText: text, annotationElement: element}
  }, [file.fileName, totalCommentsCount, maxAnnotation])

  useEffect(() => {
    if (rowRef.current && file.diff.pathDigest === hash) {
      const timeout = window.setTimeout(() => {
        //this is obviously brittle and not testable, but is the cleanest solution to avoid jumpiness
        const fileTreeParent = ssrSafeDocument?.getElementById(DIFF_FILE_TREE_ID)?.parentElement
        const rowHeight = rowRef.current!.offsetTop
        const innerWindowHeight = ssrSafeWindow?.innerHeight ?? 0
        const usableHeight = innerWindowHeight / 2
        if (fileTreeParent) fileTreeParent.scrollTop = rowHeight - usableHeight
      }, 0)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [file.diff.pathDigest, hash])

  // Navigation logic ONLY happens on anchor/link click
  const onLinkClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const mouseEvent = e

      // Modifier keys or middle click: open in new tab
      if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.button === 1) {
        mouseEvent.preventDefault()
        window.open(anchorHref, '_blank')
        return
      }
    },
    [anchorHref],
  )

  const onSelectItem = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      // Keyboard events: trigger selection only
      if (e.nativeEvent instanceof KeyboardEvent) {
        const keyboardEvent = e as KeyboardEvent<HTMLElement>
        // Do nothing if item isn't currently selected
        if (rowRef?.current !== document.activeElement) {
          keyboardEvent.preventDefault()
          return
        }
        // Trigger primary action when Enter or Space are pressed
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault()
          onSelect?.()
          anchorRef?.current?.click()
          return
        }
      }
      if (e.nativeEvent instanceof MouseEvent) {
        const mouseEvent = e as MouseEvent<HTMLElement>
        // Open link in a new tab on click with command or control pressed or middle click
        if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.button === 1) {
          mouseEvent.preventDefault()
          onLinkClick(mouseEvent) // reuse the same navigation behavior
          return
        }
        onSelect?.()
        anchorRef?.current?.click()
      }
    },
    [onLinkClick, onSelect],
  )

  const fileName = file.fileName ?? ''

  const fileLinkElement = (
    <Link
      href={anchorHref}
      muted
      ref={anchorRef}
      // Intentionally setting the role to presentation to prevent link from being announced by assistive tech
      role="presentation"
      className="fgColor-default"
      // Intentionally setting tabIndex to -1 to prevent link from being focusable by assistive tech
      tabIndex={-1}
      // Prevent the link from being focused or clicked separately from the TreeView.Item
      onClick={onLinkClick}
    >
      {fileName}
    </Link>
  )

  return (
    <>
      <TreeView.Item
        defaultExpanded
        aria-label={screenReaderText}
        aria-level={depth}
        current={file.diff.pathDigest === hash}
        id={file.diff.path}
        key={file.diff.pathDigest}
        onSelect={onSelectItem}
        ref={rowRef}
        className={styles['file-tree-row']}
      >
        <TreeView.LeadingVisual>
          <FileStatusIconItem changeType={file.diff.changeType} isSymlink={file.diff.isSymlink} />
        </TreeView.LeadingVisual>
        {showTooltip ? (
          <Tooltip text={fileName} direction="ne">
            {fileLinkElement}
          </Tooltip>
        ) : (
          fileLinkElement
        )}
        {/* Don't show the unresolved comment and annotation count if it's 0 */}
        {(!!totalCommentsCount || maxAnnotation) && (
          <TreeView.TrailingVisual>
            <div className="d-flex flex-items-center flex-row">
              {!!totalCommentsCount && (
                <>
                  <CommentIcon />
                  <div className="ml-1 text-bold fgColor-default f6">
                    {totalCommentsCount < 10 ? totalCommentsCount : '9+'}
                  </div>
                </>
              )}
              {annotationElement}
            </div>
          </TreeView.TrailingVisual>
        )}
      </TreeView.Item>
    </>
  )
})

interface DirectoryProps extends Pick<FileProps, 'onSelect' | 'hash'> {
  directory: DirectoryNode<DiffDelta>
  depth?: number
  /**
   * Overrides the default render of <File> to allow for data modification or a custom element.
   * When using this, one should try to return a <File> element from this file or something analogous in DOM structure
   */
  fileNodeRenderer?: (props: FileProps) => ReactNode
  leadingPath?: string
  /**
   * Sorts the files and directories in the order they were provided in the `diffs` prop when true.
   * If false, performs client-side sorting alphabetically with uppercase letters first.
   */
  sortByDiffOrder: boolean
}

const Directory = memo(function Directory({
  directory,
  depth = 0,
  leadingPath = '',
  sortByDiffOrder,
  onSelect,
  hash,
  fileNodeRenderer,
}: DirectoryProps): ReactElement {
  const pathPrefix = leadingPath ? `${leadingPath}/` : ''

  const rowRef = useRef<HTMLElement | null>(null)

  // Using listItemRef to fix a bug where two tooltips will stay rendered at the same time
  const listItemRef = useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: listItemRef, mouseRowRef: rowRef})

  // collapse this directory if it has no files and only one child directory
  if (!directory.files.length && directory.directories.length === 1) {
    return (
      <>
        {directory.directories.map(subDirectory => (
          <Directory
            key={subDirectory.path}
            /* Set depth to one for top-level directories that are collapsed so that tree styling is applied correctly */
            depth={depth === 0 ? 1 : depth}
            directory={subDirectory}
            leadingPath={`${pathPrefix}${directory.name}`}
            sortByDiffOrder={sortByDiffOrder}
            onSelect={onSelect}
            hash={hash}
            fileNodeRenderer={fileNodeRenderer}
          />
        ))}
      </>
    )
  }

  function renderDirectoryContents() {
    return (
      <DirectoryRendering
        directory={directory}
        depth={depth}
        sortByDiffOrder={sortByDiffOrder}
        onSelect={onSelect}
        hash={hash}
        fileNodeRenderer={fileNodeRenderer}
      />
    )
  }

  if (depth === 0) {
    return renderDirectoryContents()
  }

  return (
    <TreeView.Item ref={listItemRef} key={directory.path} defaultExpanded id={directory.path}>
      <TreeView.LeadingVisual>
        <TreeView.DirectoryIcon />
      </TreeView.LeadingVisual>
      <span ref={rowRef}>{`${pathPrefix}${directory.name}`}</span>
      {showTooltip && (
        <PortalTooltip
          data-testid={`${directory.name}-directory-item-tooltip`}
          id={`${directory.name}-directory-item-tooltip`}
          contentRef={listItemRef}
          aria-label={`${pathPrefix}${directory.name}`}
          open
          direction="ne"
        />
      )}
      <TreeView.SubTree>{renderDirectoryContents()}</TreeView.SubTree>
    </TreeView.Item>
  )
})

type MergedEntries = {
  path: string
  pathType: string
  node: FileNode<DiffDelta> | DirectoryNode<DiffDelta>
  sortOrder: number
}

const DirectoryRendering = memo(function DirectoryRendering({
  directory,
  fileNodeRenderer,
  sortByDiffOrder,
  depth = 0,
  onSelect,
  hash,
}: DirectoryProps) {
  // Memoize the merged and sorted paths to prevent re-sorting on every render
  const mergedPaths: MergedEntries[] = useMemo(() => {
    // merge files and directories into a single array
    const paths: MergedEntries[] = directory.files
      .map(file => {
        return {path: file.filePath, pathType: 'file', node: file, sortOrder: file.sortOrder}
      })
      .concat(
        directory.directories.map(dir => {
          return {
            path: dir.path,
            pathType: 'directory',
            node: dir as unknown as FileNode<DiffDelta>,
            sortOrder: dir.sortOrder,
          }
        }),
      )

    if (sortByDiffOrder) {
      paths.sort((a, b) => {
        return a.sortOrder - b.sortOrder
      })
    } else {
      // sort the merged paths by path to render them in alphabetical order
      paths.sort((a, b) => caseFirstCompare(a.path, b.path))
    }

    return paths
  }, [directory.files, directory.directories, sortByDiffOrder])

  return (
    <>
      {mergedPaths.map(entry => {
        if (entry.pathType === 'file') {
          if (fileNodeRenderer) {
            return fileNodeRenderer({onSelect, hash, file: entry.node as FileNode<DiffDelta>, depth: depth + 1})
          }
          return (
            <File
              key={entry.path}
              depth={depth + 1}
              file={entry.node as FileNode<DiffDelta>}
              onSelect={onSelect}
              hash={hash}
            />
          )
        } else {
          return (
            <Directory
              key={entry.path}
              depth={depth + 1}
              directory={entry.node as DirectoryNode<DiffDelta>}
              fileNodeRenderer={fileNodeRenderer}
              sortByDiffOrder={sortByDiffOrder}
              onSelect={onSelect}
              hash={hash}
            />
          )
        }
      })}
    </>
  )
})

export interface DiffFileTreeProps
  extends Pick<FileProps, 'onSelect'>,
    Pick<DirectoryProps, 'fileNodeRenderer' | 'sortByDiffOrder'> {
  diffs: Readonly<Array<Readonly<DiffDelta>>>
  className?: string
}

export const DiffFileTree = memo(function FileTree({
  diffs,
  fileNodeRenderer,
  sortByDiffOrder,
  className,
  onSelect,
}: DiffFileTreeProps) {
  const fileTree = useMemo(() => getFileTree<DiffDelta>(diffs), [diffs])
  const {selectedPathDigest} = useSelectedDiffHash()

  return (
    <TreeView aria-label="File Tree" className={className}>
      <Directory
        directory={fileTree}
        fileNodeRenderer={fileNodeRenderer}
        sortByDiffOrder={sortByDiffOrder}
        onSelect={onSelect}
        hash={selectedPathDigest}
      />
    </TreeView>
  )
})

try{ File.displayName ||= 'File' } catch {}
try{ Directory.displayName ||= 'Directory' } catch {}
try{ DirectoryRendering.displayName ||= 'DirectoryRendering' } catch {}
try{ DiffFileTree.displayName ||= 'DiffFileTree' } catch {}